/**
 * ImageCollectionSubTab Component
 *
 * Main orchestrator for "Bildersammlung" (Image Collection).
 * Shows all images ever linked to a product (current + historical) with filtering.
 */
import { useState } from 'react';
import { useImageCollection } from '@/hooks/useImageCollection';
import { ImageCollectionFilters } from './ImageCollectionFilters';
import { ImageCollectionGrid } from './ImageCollectionGrid';
import { ImageVersionHistorySheet } from './ImageVersionHistorySheet';

interface ImageCollectionSubTabProps {
  productId: string;
}

export function ImageCollectionSubTab({ productId }: ImageCollectionSubTabProps) {
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

  const collection = useImageCollection({
    productId,
    enabled: true,
  });

  const handleImageClick = (assetId: string, linkId?: string) => {
    setSelectedAssetId(assetId);
    setSelectedLinkId(linkId || null);
  };

  const handleCloseVersionHistory = () => {
    setSelectedLinkId(null);
    setSelectedAssetId(null);
  };

  return (
    <div className="flex h-full">
      {/* Filters Sidebar */}
      <div className="w-64 border-r border-neutral-200 dark:border-neutral-700 p-4 overflow-y-auto">
        <ImageCollectionFilters
          filters={collection.filters}
          filterOptions={collection.filterOptions}
          activeFilterCount={collection.activeFilterCount}
          onUpdateFilters={collection.updateFilters}
          onClearFilters={collection.clearFilters}
          isLoading={collection.isLoadingFilters}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <ImageCollectionGrid
          images={collection.images}
          isLoading={collection.isLoading}
          isFetching={collection.isFetching}
          error={collection.error}
          onImageClick={handleImageClick}
          page={collection.page}
          totalPages={collection.totalPages}
          totalCount={collection.totalCount}
          onPageChange={(page) => collection.updateFilters({ page })}
        />
      </div>

      {/* Version History Sheet */}
      {selectedLinkId && (
        <ImageVersionHistorySheet
          productId={productId}
          linkId={selectedLinkId}
          assetId={selectedAssetId}
          isOpen={!!selectedLinkId}
          onClose={handleCloseVersionHistory}
          onRollbackSuccess={() => collection.refetch()}
        />
      )}
    </div>
  );
}
